<template>
	<b-field :label="$t('NotificationGroups')" v-if="showComponent" data-test-id="NotificationGroupsMultiContainer">
		<b-taginput
			ref="taginputRef"
			v-model="selectedGroups"
			:data="filteredGroupList"
			field="groupDisplay"
			autocomplete
			:allow-new="false"
			:open-on-focus="true"
			:closable="canEdit"
			icon-pack="fas"
			icon="search"
			:placeholder="canEdit ? $t('ChooseGroup') : ''"
			@remove="onGroupRemoved"
			:disabled="!canEdit"
			@typing="getFilteredGroupList"
			data-test-id="NotificationGroupsMulti"
			:loading="shouldDisplayLoading"
		>
			<template #header>
				<a
					class="has-text-info is-size-6"
					@click="createGroup"
					data-test-id="groupCreation"
				>
					{{ $t("CreateGroup") }}
				</a>
			</template>
			<template #default="props">
				<strong>{{ props.option.groupId }}</strong>
				: {{ props.option.groupName }}
			</template>
			<template #empty>{{ $t("NoItemsToShow") }}</template>
			<template #selected="props">
				<b-tag
					v-for="(tag, index) in props.tags"
					:key="index"
					:type="getType(tag)"
					ellipsis
					closable
					@close="taginputRef?.removeTag(index, $event)"
				>
					<strong>{{ tag.groupId }}</strong>
					: {{ tag.groupName }}
				</b-tag>
			</template>
		</b-taginput>
	</b-field>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAppStore } from '@/stores/app'
import notificationService from '@/services/notificationService'
import type { NotificationGroup } from '@/stores/app'

// Types
interface GroupListItem {
  groupId: number
  groupName: string
  groupType: string
  groupDisplay: string
}

interface TagInputInstance {
  removeTag: (index: number, event: Event) => void
  [key: string]: any
}

// Props
interface Props {
  modelValue: GroupListItem[]
  canEdit: boolean
  editableTypes: string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  canEdit: false,
  editableTypes: () => []
})

// Emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: GroupListItem[]): void
  (e: 'unauthorize', value: GroupListItem): void
  (e: 'create-new-group'): void
}>()

// Composables
const store = useAppStore()

// Refs
const taginputRef = ref<TagInputInstance | null>(null)
const searchText = ref<string>('')

// Computed
const notificationGroupList = computed(() => store.getNotificationGroups)

const selectedGroups = computed({
  get(): GroupListItem[] {
    return props.modelValue
  },
  set(value: GroupListItem[]): void {
    emit('update:modelValue', value)
  }
})

const filteredGroupList = computed((): GroupListItem[] => {
  const groups = groupsList.value
  if (!groups) return []
  
  return groups.filter(
    (option) =>
      option.groupName
        .toString()
        .concat(option.groupId.toString())
        .toLowerCase()
        .indexOf(searchText.value.toLowerCase()) >= 0
  )
})

const shouldDisplayLoading = computed((): boolean => {
  return (
    !Array.isArray(notificationGroupList.value) ||
    !notificationGroupList.value.length
  )
})

const groupsList = computed((): GroupListItem[] => {
  if (!notificationGroupList.value) return []
  
  const distinctGroupList: GroupListItem[] = []
  
  notificationGroupList.value.forEach((element: NotificationGroup) => {
    const groupExists = distinctGroupList.find(
      (g) => g.groupId === element.groupId
    )
    
    if (groupExists === undefined) {
      distinctGroupList.push({
        groupId: element.groupId,
        groupName: element.groupName,
        groupType: "Shop",
        groupDisplay: element.groupId
          .toString()
          .concat(" ")
          .concat(element.groupName),
      })
    }
  })
  
  return distinctGroupList.filter(
    (group) =>
      !selectedGroups.value.some((g) => g.groupId === group.groupId)
  )
})

const showComponent = computed((): boolean => {
  return selectedGroups.value.length > 0 || props.canEdit
})

// Methods
const getFilteredGroupList = (text: string): void => {
  searchText.value = text
}

const onGroupRemoved = (value: GroupListItem): void => {
  if (!props.editableTypes.some((t) => t === value.groupType)) {
    emit('unauthorize', value)
    selectedGroups.value.unshift(value)
  }
}

const getType = (tag: GroupListItem): string => {
  if (tag.groupType === "Shop") {
    return "is-blue"
  }
  if (tag.groupType === "Chain") {
    return "is-green"
  }
  if (tag.groupType === "ShoppingCenter") {
    return "is-red"
  }
  return "is-primary"
}

const createGroup = (): void => {
  emit('create-new-group')
}

// Lifecycle
onMounted(async (): Promise<void> => {
  if (
    notificationGroupList.value === null ||
    notificationGroupList.value.length === 0
  ) {
    await notificationService.getNotificationGroups()
  }
})
</script>
